import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  selectionCount,
  type FilterState,
  type MonthOption,
  type Selection,
} from '@/filters';

interface FilterBarProps {
  state: FilterState;
  categories: readonly string[];
  submitters: Array<{ submitterId: string; submitterAlias: string }>;
  months: MonthOption[];
  onChange: (next: FilterState) => void;
  onSearchChange: (raw: string) => void;
}

/**
 * Toggle membership of `value` within a selection. `'all'` is expanded to the
 * full `universe` first (so removing one item leaves the rest selected). If
 * the resulting array covers the whole universe we collapse back to `'all'`;
 * an empty result stays an explicit `[]` (matches nothing).
 */
function toggleSelection(
  sel: Selection,
  value: string,
  universe: readonly string[],
): Selection {
  const current = sel === 'all' ? [...universe] : [...sel];
  const idx = current.indexOf(value);
  if (idx >= 0) {
    current.splice(idx, 1);
  } else {
    current.push(value);
  }
  if (universe.length > 0 && current.length === universe.length) return 'all';
  return current;
}

const triggerClass =
  'inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:pointer-events-none disabled:opacity-50';

export function FilterBar({
  state,
  categories,
  submitters,
  months,
  onChange,
  onSearchChange,
}: FilterBarProps) {
  const [searchInput, setSearchInput] = React.useState(state.search);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Keep the local input in sync when search is changed externally (e.g. the
  // "Clear filters" action resetting to default state).
  React.useEffect(() => {
    setSearchInput(state.search);
  }, [state.search]);

  React.useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const debouncedEmit = (raw: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearchChange(raw), 200);
  };

  const catCount = selectionCount(state.categories, categories.length);
  const subUniverse = submitters.map((s) => s.submitterId);
  const subCount = selectionCount(state.submitters, submitters.length);

  const selectedMonth =
    months.find((m) => m.key === state.month)?.label ?? state.month;

  return (
    <div className="sticky top-14 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div
        role="search"
        className="mx-auto flex max-w-[1280px] flex-wrap items-center gap-3 px-6 py-3"
      >
        {/* Category multiselect */}
        <DropdownMenu>
          <DropdownMenuTrigger aria-label="Filter categories" className={triggerClass}>
            Categories
            <Badge variant="secondary">
              {catCount.selected} / {catCount.total}
            </Badge>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[220px]">
            {categories.map((cat) => (
              <DropdownMenuCheckboxItem
                key={cat}
                checked={state.categories === 'all' || state.categories.includes(cat)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() =>
                  onChange({
                    ...state,
                    categories: toggleSelection(state.categories, cat, categories),
                  })
                }
              >
                {cat}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Submitter multiselect */}
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Filter submitters"
            className={triggerClass}
            disabled={submitters.length === 0}
          >
            Submitters
            <Badge variant="secondary">
              {subCount.selected} / {subCount.total}
            </Badge>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[220px]">
            {submitters.map((s) => (
              <DropdownMenuCheckboxItem
                key={s.submitterId}
                checked={
                  state.submitters === 'all' ||
                  state.submitters.includes(s.submitterId)
                }
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() =>
                  onChange({
                    ...state,
                    submitters: toggleSelection(
                      state.submitters,
                      s.submitterId,
                      subUniverse,
                    ),
                  })
                }
              >
                {s.submitterAlias}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Month picker */}
        <DropdownMenu>
          <DropdownMenuTrigger aria-label="Select month" className={triggerClass}>
            {selectedMonth}
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[180px]">
            <DropdownMenuRadioGroup
              value={state.month}
              onValueChange={(m) => onChange({ ...state, month: m })}
            >
              {months.map((m) => (
                <DropdownMenuRadioItem key={m.key} value={m.key}>
                  {m.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/*
          Search. AOZONE-114: the placeholder deliberately does NOT enumerate
          the fields matchesSearch reads. An earlier revision of this change
          tried ("Search customer, narrative, title, or action…") and it
          truncated at this control's actual width (w-full sm:w-64) — the list
          was already four items and only grows, so enumeration is a
          structurally losing strategy, not merely one that was too long this
          time. Widening the input is not the fix either: this control shares a
          responsive row with three others, so it would trade a cosmetic clip
          for a real layout regression at small widths. Short and true instead;
          `aria-label` already carries the accessible name.
        */}
        <Input
          type="search"
          aria-label="Search entries"
          placeholder="Search entry text…"
          className={cn('w-full sm:w-64')}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            debouncedEmit(e.target.value);
          }}
        />
      </div>
    </div>
  );
}
